import {
  createAddressTitle,
  createAreaIdentifierString,
  getDisplayPrice,
  type ParsedAsset,
} from '@aikoa/common'
import { useTranslations, type Language } from '@aikoa/i18n/react'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { type IconName } from '~/components/Icon'
import { getBuildingTypeLabel } from '~/lib/assets'

export type PropertyItemDisplayType = 'inline' | 'distinct'

export type BaseProps = {
  asset: ParsedAsset
  className?: string
  isLoading?: boolean
  display?: PropertyItemDisplayType
  showAddPrice?: boolean
  onPriceAdded?: () => void
}

export const formatPropertyPrice = (
  price: string | number,
  currency: string,
  language: Language
) => {
  const localeMap: Record<string, string> = {
    fi: 'fi-FI',
    en: 'en-US',
    es: 'es-ES',
    sv: 'sv-SE',
  }
  const locale = localeMap[language] || 'fi-FI'

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(Number.parseFloat(String(price)))
}

export function collectAssetDetails(asset: ParsedAsset) {
  const addressTitle = createAddressTitle(asset.address, false) || asset.name
  const areaString = createAreaIdentifierString(asset.address)
  const askingPrice = getDisplayPrice(asset.prices || [])

  return { addressTitle, areaString, askingPrice }
}

export function useStats(asset: ParsedAsset, short?: boolean) {
  const { t } = useTranslations('property')

  return useMemo(() => {
    const size = asset.data?.units?.[0]?.size
    const buildYear = asset.data?.units?.[0]?.buildYear
    const rooms = asset.data?.units?.[0]?.rooms

    return !asset.data
      ? []
      : [
          {
            key: 'propertyType',
            icon: 'home-outline' as IconName,
            value: getBuildingTypeLabel({
              buildingType: asset.data.propertyType,
              t,
              short,
            }),
          },
          rooms && {
            key: 'rooms',
            icon: 'grid-outline' as IconName,
            value: asset.data.units?.[0]?.rooms
              ? `${asset.data.units[0].rooms} ${t(($) => $.stats.rooms)}`
              : '',
          },
          size && {
            key: 'size',
            icon: 'square-outline' as IconName,
            value: asset.data.units?.[0]?.size
              ? `${asset.data.units[0].size} ${t(($) => $.units.squareMeters)}`
              : '',
          },
          buildYear && {
            key: 'buildYear',
            icon: 'build-outline' as IconName,
            value: asset.data.units?.[0]?.buildYear ? asset.data.units[0].buildYear : '',
          },
          {
            key: 'createdAt',
            icon: 'calendar-outline' as IconName,
            value: dayjs(asset.created_at).format('DD.MM.YYYY'),
          },
        ].filter(Boolean)
  }, [asset, t, short]) as {
    key: string
    icon: IconName
    value: string | number
  }[]
}
